import { TFunction } from 'i18next';
import { AnySchema, ValidationError } from 'yup';
import {
  FeatureEnum,
  ModelNamesBesidesFeatureEnums,
} from '../services/data/impl/cube/cubeSlice';
import { UnitsEnum } from '../services/entities/impl/blfApi/apiConstants';
import { User } from '../services/entities/impl/blfApi/schemas/user';
import { useTranslation } from '../services/translation/hooks/useTranslation';
import { ScanDataEnum } from './scanDataApi/scanDataDefinitions';
import { useUnits } from './settings/hooks/useUnits';

// Meters to various units conversion factors
export const METERS_TO_FEET_CONVERSION_FACTOR = 3.28084;
export const METERS_TO_INCHES_CONVERSION_FACTOR = 39.3701;
export const METER_TO_CENTIMETER_CONVERSION_FACTOR = 100;
export const METERS_TO_MILLIMETERS_CONVERSION_FACTOR = 1000;
// Feet to various units conversion factors
export const FEET_TO_METERS_CONVERSION_FACTOR = 0.3048;
export const FEET_TO_CENTIMETER_CONVERSION_FACTOR = 30.48;
export const FEET_TO_MILLIMETER_CONVERSION_FACTOR = 304.8;
export const FEET_TO_INCHES_CONVERSION_FACTOR = 12;

/**
 * Takes in an array of any type and returns a random item from it.
 * @param arr Input array
 * @returns Random item from array
 */
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const getRandomArrayItem = (arr: any[]) => {
  return arr[Math.floor(Math.random() * arr.length)];
};

/**
 * Calculates the average of an array of numbers.
 *
 * @param numbers An array of numbers
 * @returns The average of the numbers in the array
 */
export function calculateAverage(numbers: number[]): number {
  if (numbers.length === 0) return 0;

  let sum = 0;
  for (const number of numbers) {
    sum += number;
  }
  return sum / numbers.length;
}

/**
 * Check if the user has any of the authorized roles
 * @param authorizedRoles A string array of authorized roles
 * @param user A user object with a roles property containing an array of role objects
 * @returns Boolean indicating if the user has any of the authorized roles
 */
export function hasAuthorizedRole(
  authorizedRoles: string[],
  user: User | undefined | null
): boolean {
  if (!user) return false;
  return user.roles.some((role) => authorizedRoles.includes(role.name));
}

/**
 * Check if a property exists in an object.
 *
 * @param {object} obj The object to check.
 * @param {string} prop The property to search for.
 * @returns {boolean} Returns true if the property exists in the object, and false otherwise.
 */
export function hasProperty(obj: object | undefined, prop: string): boolean {
  if (!obj) {
    return false;
  }
  return prop in obj;
}

/**
 * Darkens a given hex color by mixing it with black by a percentage.
 *
 * @param color - A string representing a hex color value, including the `#` symbol.
 * @param percent - A number between 0 and 1 representing the percentage of black to mix with the color.
 * @returns A string representing the resulting darker hex color value, including the `#` symbol.
 */
export function darkenColor(color: string, percent: number): string {
  // Remove # from color if present
  color = color.replace('#', '');

  // Convert color to RGB
  const red = parseInt(color.substring(0, 2), 16);
  const green = parseInt(color.substring(2, 4), 16);
  const blue = parseInt(color.substring(4, 6), 16);

  // Calculate new RGB values based on percentage
  const newRed = Math.floor(red * (1 - percent));
  const newGreen = Math.floor(green * (1 - percent));
  const newBlue = Math.floor(blue * (1 - percent));

  // Convert back to hex color
  const newColor = `#${newRed.toString(16)}${newGreen.toString(
    16
  )}${newBlue.toString(16)}`;

  return newColor;
}

/**
 * Supported unit conversion types from meters.
 */
export type ConvertMeterType =
  | 'Feet'
  | 'Inches'
  | 'Centimeters'
  | 'Millimeters';

/**
 * Mapping of ConvertType to their respective conversion factors.
 */
const metersToUnitsConversionMap: Record<ConvertMeterType, number> = {
  Feet: METERS_TO_FEET_CONVERSION_FACTOR,
  Inches: METERS_TO_INCHES_CONVERSION_FACTOR,
  Centimeters: METER_TO_CENTIMETER_CONVERSION_FACTOR,
  Millimeters: METERS_TO_MILLIMETERS_CONVERSION_FACTOR,
};

/**
 * Meters to unit conversion function.
 *
 * Converts meters to the specified unit or value per meter to value per unit.
 *
 * @param value - The numerical value to convert.
 * @param isPerMeter - Indicates if the conversion is per meter or direct. Set true for converting value per meter, false for direct conversion.
 * @param unitType - The target unit type for conversion.
 * @returns The converted number or null if input is invalid.
 *
 * Example usage:
 *
 * Direct conversion (e.g., converting 10 meters to feet):
 * convertMetersToUnits(10, false, 'Feet') // Returns 32.8084
 *
 * Per meter conversion (e.g., converting 20 items/meter to items/foot):
 * convertMetersToUnits(20, true, 'Feet') // Returns 6.096
 *
 * Converting to millimeters:
 * convertMetersToUnits(1, false, 'Millimeters') // Returns 1000
 */
export const convertMetersToUnits = (
  value: number,
  isPerMeter: boolean,
  unitType: ConvertMeterType
): number | null => {
  if (typeof value !== 'number' || isNaN(value)) {
    console.warn(`Invalid value provided: ${value}. Must be a number.`);
    return null;
  }

  if (value === 0) return 0;

  const conversionFactor = metersToUnitsConversionMap[unitType];

  if (!conversionFactor) {
    console.warn(`Unsupported ConvertType: ${unitType}`);
    return null;
  }

  return isPerMeter ? value / conversionFactor : value * conversionFactor;
};

/**
 * Supported unit conversion types from feet.
 */
export type ConvertFeetType =
  | 'Meters'
  | 'Centimeters'
  | 'Millimeters'
  | 'Inches';

/**
 * Mapping of ConvertFeetType to their respective conversion factors.
 */
const feetToUnitsConversionMap: Record<ConvertFeetType, number> = {
  Meters: FEET_TO_METERS_CONVERSION_FACTOR,
  Centimeters: FEET_TO_CENTIMETER_CONVERSION_FACTOR,
  Millimeters: FEET_TO_MILLIMETER_CONVERSION_FACTOR,
  Inches: FEET_TO_INCHES_CONVERSION_FACTOR,
};

/**
 * Feet to unit conversion function.
 *
 * Converts feet to the specified unit or value per foot to value per unit.
 *
 * @param value - The numerical value in feet to convert.
 * @param isPerFeet - Indicates if the conversion is per foot or direct. Set true for converting value per foot, false for direct conversion.
 * @param unitType - The target unit type for conversion.
 * @returns The converted number or null if input is invalid.
 *
 * Example usage:
 *
 * Direct conversion (e.g., converting 10 feet to meters):
 * convertFeetToUnits(10, false, 'Meters') // Returns 3.048
 *
 * Per foot conversion (e.g., converting 20 items/foot to items/meter):
 * convertFeetToUnits(20, true, 'Meters') // Returns 65.616
 *
 * Converting to millimeters:
 * convertFeetToUnits(1, false, 'Millimeters') // Returns 304.8
 */
export const convertFeetToUnits = (
  value: number,
  isPerFeet: boolean,
  unitType: ConvertFeetType
): number | null => {
  if (typeof value !== 'number' || isNaN(value)) {
    console.warn(`Invalid value provided: ${value}. Must be a number.`);
    return null;
  }

  if (value === 0) return 0;

  const conversionFactor = feetToUnitsConversionMap[unitType];

  if (!conversionFactor) {
    console.warn(`Unsupported ConvertFeetType: ${unitType}`);
    return null;
  }

  return isPerFeet ? value / conversionFactor : value * conversionFactor;
};

export const ScanDataColsUnitsConversionsMap: {
  [key: string]: (units: UnitsEnum, autoAlias?: boolean) => string;
} = {
  [ScanDataEnum.count_per_meter]: (units, autoAlias = true) =>
    meterUnitConversionSql(
      ScanDataEnum.count_per_meter,
      units,
      true,
      autoAlias,
      MeasurementTypes.meterOrFeet
    ),
  [ScanDataEnum.detection_length]: (units, autoAlias = true) =>
    meterUnitConversionSql(
      ScanDataEnum.detection_length,
      units,
      false,
      autoAlias,
      MeasurementTypes.millimeterOrInch
    ),
  [ScanDataEnum.detection_area]: (units, autoAlias = true) =>
    meterUnitConversionSql(
      ScanDataEnum.detection_area,
      units,
      false,
      autoAlias,
      MeasurementTypes.millimeterOrInch
    ),
  [ScanDataEnum.traveled_distance]: (units, autoAlias = true) =>
    meterUnitConversionSql(
      ScanDataEnum.traveled_distance,
      units,
      false,
      autoAlias,
      MeasurementTypes.meterOrFeet
    ),
  [ScanDataEnum.image_captured_length]: (units, autoAlias = true) =>
    meterUnitConversionSql(
      ScanDataEnum.image_captured_length,
      units,
      false,
      autoAlias,
      MeasurementTypes.meterOrFeet
    ),
};

/**
 * Performs unit conversion on a column based on the specified conversion map.
 *
 * @param col - The column name to be converted.
 * @param units - The units to be used for conversion.
 * @param autoAlias - Flag to enable auto aliasing during conversion.
 * @returns The converted column name.
 */
export function convertSQLColumnUnits(
  col: ScanDataEnum,
  units: UnitsEnum,
  autoAlias = true
): string {
  if (col in ScanDataColsUnitsConversionsMap) {
    return ScanDataColsUnitsConversionsMap[col](units, autoAlias);
  }
  return col;
}

export interface ColumnConversion {
  original: string;
  converted: string;
}

/**
 * Converts column names based on the specified unit conversion map and options.
 *
 * @param cols - Array of column names to be converted.
 * @param units - The units to be used for conversion.
 * @param disableUnitsTransform - Flag to disable unit transformation.
 * @param useUnitsTransformSubstringMatching - Flag to enable substring matching for unit transformation.
 * @param autoAlias - Flag to enable auto aliasing during conversion.
 * @returns Array of objects each containing the original and converted column names.
 */
export function convertColumnUnits(
  cols: string[],
  units: UnitsEnum,
  disableUnitsTransform: boolean | undefined,
  useUnitsTransformSubstringMatching: boolean | undefined,
  autoAlias = true
): ColumnConversion[] {
  // Get columns that are able to be transformed based on the conversion map
  const colsAbleToTransform = Object.keys(ScanDataColsUnitsConversionsMap);

  // Convert the column names based on the specified options and conversion map
  const transformedCols = cols.map((el) => {
    let convertedValue = el;

    if (!disableUnitsTransform) {
      if (colsAbleToTransform.includes(el)) {
        convertedValue = convertSQLColumnUnits(
          el as ScanDataEnum,
          units,
          autoAlias
        );
      } else if (useUnitsTransformSubstringMatching) {
        const foundMatch = colsAbleToTransform.find((t) => el.includes(t));
        if (foundMatch) {
          convertedValue = el.replaceAll(
            foundMatch,
            convertSQLColumnUnits(foundMatch as ScanDataEnum, units, false)
          );
        }
      }
    }

    return {
      original: el,
      converted: convertedValue,
    };
  });

  return transformedCols;
}

/**
 * Convert a given number from meters to feet, centimeters to inches, millimeters to inches,
 * or from value per meter to value per foot for SQL queries.
 *
 * @param value - The number in meters to be converted to feet, or the value per meter to be converted to value per foot.
 * @param units - The unit enumeration, which determines if conversion should take place.
 * @param perMeter - A boolean parameter. Set true for converting value per meter, false for direct conversion.
 * @param autoAlias - A boolean parameter. Set true if you need to automatically extract alias from expression.
 * @param measurementType - The type of measurement conversion to be carried out.
 * @returns The converted number in feet, inches or value per foot, or the original value if UnitsEnum is not imperial.
 *
 * Example:
 * Direct conversion (e.g., length, width, height):
 * meterUnitConversionSql(10, UnitsEnum.imperial, false, false, MeasurementTypes.meterOrFeet) => '10 * 3.28084'
 *
 * Value per meter conversion (e.g., value per unit of length):
 * meterUnitConversionSql(20, UnitsEnum.imperial, true, false, MeasurementTypes.meterOrFeet) => '20 / 3.28084'
 */
export function meterUnitConversionSql(
  value: string,
  units: UnitsEnum,
  perMeter: boolean,
  autoAlias = true,
  measurementType = MeasurementTypes.meterOrFeet
): string {
  const squareMetersToSquareFeets = METERS_TO_FEET_CONVERSION_FACTOR ** 2;

  if (units === UnitsEnum.imperial) {
    if (
      measurementType === MeasurementTypes.millimeterOrInch ||
      measurementType === MeasurementTypes.centimeterOrInch
    ) {
      if (perMeter) {
        return `${value} / ${METERS_TO_INCHES_CONVERSION_FACTOR} ${
          autoAlias ? `as ${value}` : ''
        }`;
      } else {
        return `${value} * ${METERS_TO_INCHES_CONVERSION_FACTOR} ${
          autoAlias ? `as ${value}` : ''
        }`;
      }
    }

    if (measurementType === MeasurementTypes.metersOrFeetSquared) {
      if (perMeter) {
        return `${value} / ${squareMetersToSquareFeets} ${
          autoAlias ? `as ${value}` : ''
        }`;
      }
      return `${value} * ${squareMetersToSquareFeets} ${
        autoAlias ? `as ${value}` : ''
      }`;
    }

    if (perMeter) {
      return `${value} / ${METERS_TO_FEET_CONVERSION_FACTOR} ${
        autoAlias ? `as ${value}` : ''
      }`;
    } else {
      return `${value} * ${METERS_TO_FEET_CONVERSION_FACTOR} ${
        autoAlias ? `as ${value}` : ''
      }`;
    }
  } else {
    if (measurementType === MeasurementTypes.centimeterOrInch) {
      if (perMeter) {
        return `${value} / ${METER_TO_CENTIMETER_CONVERSION_FACTOR} ${
          autoAlias ? `as ${value}` : ''
        }`;
      }
      return `${value} * ${METER_TO_CENTIMETER_CONVERSION_FACTOR} ${
        autoAlias ? `as ${value}` : ''
      }`;
    } else if (measurementType === MeasurementTypes.millimeterOrInch) {
      if (perMeter) {
        return `${value} / ${METERS_TO_MILLIMETERS_CONVERSION_FACTOR} ${
          autoAlias ? `as ${value}` : ''
        }`;
      }
      return `${value} * ${METERS_TO_MILLIMETERS_CONVERSION_FACTOR} ${
        autoAlias ? `as ${value}` : ''
      }`;
    }
    return `${value} ${autoAlias ? `as ${value}` : ''}`;
  }
}

/**
 * Type guard function to check if the given value is a valid key of the `FeatureEnum` type
 * or of the `ModelNamesBesidesFeatureEnums` if `checkModelNames` is true.
 *
 * @param value - The string value to be checked.
 * @param checkModelNames - A boolean to indicate whether to include `ModelNamesBesidesFeatureEnums` in the check.
 * @returns A boolean indicating if the given value is a valid key of the applicable types.
 */
export function isFeatureEnum(
  value: string | null,
  checkModelNames = false
): value is FeatureEnum {
  if (!value) return false;

  // Combine the FeatureEnum and ModelNamesBesidesFeatureEnums if checkModelNames is true
  const validEnumValues = (Object.values(FeatureEnum) as string[]).concat(
    checkModelNames
      ? (Object.values(ModelNamesBesidesFeatureEnums) as string[])
      : []
  );

  // Check for exact match
  return validEnumValues.includes(value as string);
}

export enum MeasurementTypes {
  meterOrFeet = 'meterOrFeet',
  metersOrFeetSquared = 'metersOrFeetSquared',
  centimeterOrInch = 'centimeterOrInch',
  millimeterOrInch = 'millimeterOrInch',
}

/**
 * Function to get the corresponding unit symbol for a given measurement type and unit system.
 * @param units - The unit system. It should be a value from the UnitsEnum enumeration.
 * @param measurementType - The type of measurement. Currently, it can be 'millimeter', 'meter', or 'metersSquared'.
 * @param t - The translation function from i18next.
 * @returns - The unit symbol as a string. It can be 'm', 'ft', 'm²', 'ft²', mm, etc...
 */
export function getUnitSymbol(
  units: UnitsEnum,
  measurementType: MeasurementTypes,
  t: TFunction
): string {
  // Mapping from measurement type to unit symbols in the metric system
  const metricSymbols = {
    [MeasurementTypes.meterOrFeet]: t('meterShortForm', { ns: 'common' }),
    [MeasurementTypes.metersOrFeetSquared]: t('squareMetersShortForm', {
      ns: 'common',
    }),
    [MeasurementTypes.centimeterOrInch]: t('centimeterShortForm', {
      ns: 'common',
    }),
    [MeasurementTypes.millimeterOrInch]: t('millimeterShortForm', {
      ns: 'common',
    }),
    // More measurement types can be added here in the future
  };

  // Mapping from measurement type to unit symbols in the imperial system
  const imperialSymbols = {
    [MeasurementTypes.meterOrFeet]: t('feetShortForm', { ns: 'common' }),
    [MeasurementTypes.metersOrFeetSquared]: t('squareFeetShortForm', {
      ns: 'common',
    }),
    // cm and mm both convert to inches in imperial
    [MeasurementTypes.centimeterOrInch]: t('inchShortForm', {
      ns: 'common',
    }),
    [MeasurementTypes.millimeterOrInch]: t('inchShortForm', {
      ns: 'common',
    }),
    // More measurement types can be added here in the future
  };

  // Choose the correct mapping based on the unit system
  const symbols = units === UnitsEnum.metric ? metricSymbols : imperialSymbols;

  // Return the unit symbol for the given measurement type
  return symbols[measurementType];
}

/**
 * Custom hook to obtain the unit symbol for a specified measurement type.
 *
 * This hook first retrieves the units settings from the application state using the `useAppSelector` hook,
 * and then utilizes the `useTranslation` hook to get the translation function.
 * Finally, it invokes the `getUnitSymbol` utility function with the units settings, measurement type,
 * and translation function to obtain the appropriate unit symbol.
 *
 * @param {MeasurementTypes} measurementType - The type of measurement for which to obtain the unit symbol.
 * @returns {string} The unit symbol for the specified measurement type.
 */
export const useUnitSymbol = (measurementType: MeasurementTypes) => {
  const units = useUnits();
  const { t } = useTranslation();
  return getUnitSymbol(units, measurementType, t);
};

/**
 * Capitalizes only the first letters of each word in a string, while lowercasing all other letters.
 * @param input - The string to be transformed.
 * @returns The transformed string with only the first letter of each word capitalized.
 */
export function capitalizeFirstLettersOnly(
  input: string | null | undefined
): string {
  if (!input) return '';
  return input
    .toLowerCase() // Convert the entire string to lowercase
    .split(' ') // Split the string into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(' '); // Rejoin the words into a single string
}

/**
 * Validates an object against a Yup schema and returns an object containing
 * a boolean indicating if there were any errors and an array of error strings.
 *
 * @param schema - The Yup validation schema.
 * @param objectToValidate - The object to validate.
 * @returns An object containing a boolean indicating if there were any errors and an array of error strings.
 */
export function validateWithYup(
  schema: AnySchema,
  objectToValidate: object
): { hasError: boolean; errors: string[] } {
  try {
    // Validate the object against the schema.
    // If validation fails, it will throw an error.
    schema.validateSync(objectToValidate, { abortEarly: false });

    // If validation is successful, return false for hasError and null for errors.
    return { hasError: false, errors: [] };
  } catch (validationError) {
    // Check if it's an instance of Yup's ValidationError.
    if (
      validationError instanceof ValidationError &&
      'errors' in validationError
    ) {
      // Get list of error messages.
      const errorMessages: string[] = validationError.errors;

      // console.error(
      //   'Validation error.  Object checked: ',
      //   objectToValidate,
      //   'Errors: ',
      //   errorMessages
      // );

      // Return true for hasError and the list of errors.
      return { hasError: true, errors: errorMessages };
    }
    // If the error is not a Yup ValidationError, rethrow it.
    throw validationError;
  }
}

/**
 * This function takes a search value and an object and checks if the search value matches
 * any of the searchable fields in the object.
 *
 * For fields that end with 'id', a full uuid string search is required.
 *
 * @param searchValue - the search value entered by the user
 * @param searchObject - the object that should be searched
 * @param searchableFields - array of fields that should be searched
 *
 * @returns - boolean indicating whether a match was found or not
 */
export const searchFields = <T>(
  searchValue: string,
  searchObject: T | null,
  searchableFields: (keyof T)[]
): boolean => {
  const searchVal = searchValue.toLowerCase();

  if (searchVal === '') {
    // if search box is empty, mark as found
    return true;
  }

  if (!searchObject) {
    return false;
  }

  // Check each searchable field for a match
  return searchableFields.some((field) => {
    if (
      typeof field === 'string' &&
      !Object.prototype.hasOwnProperty.call(searchObject, field)
    ) {
      console.log(
        `Search field "${field}" doesn't exist on this object, check that searchable field matches a key in the card object you are trying to search.`
      );
      return false;
    }

    const fieldToSearch = searchObject[field];

    // Ensure the field is not null and is a string before calling toLowerCase
    if (fieldToSearch && typeof fieldToSearch === 'string') {
      // If the field ends with 'id', perform a full string comparison, else perform a contains comparison, make an exception for cameraID since it's not a uuid
      if (
        field.toString().toLowerCase().endsWith('id') &&
        field.toString() !== 'cameraID'
      ) {
        return fieldToSearch === searchVal;
      } else {
        return fieldToSearch.toLowerCase().includes(searchVal);
      }
    }

    return false;
  });
};
